import { action, computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { JsonConditionItem } from 'cio/utils/conditions/json-attributes';
import AttributeCondition from './attribute';
import { ConditionType } from 'cio/utils/condition-types';

export enum ObjectAttributeSource {
  ObjectAttribute = 'related_object_attribute',
  RelationshipAttribute = 'relationship_attribute',
}

export default class ObjectAttributeCondition extends AttributeCondition {
  type: ConditionType = ConditionType.ObjectAttribute;

  objectAttributeSource = ObjectAttributeSource.ObjectAttribute;

  trackedAttributes = [
    'objectTypeId',
    'name',
    'operator',
    'comparisonOperator',
    'value',
    'value2',
    'timestampValue',
    'timeDirection',
    'objectAttributeSource',
  ];

  @tracked objectTypeId: string | null = null;

  _editableCondition: ObjectAttributeCondition | null = null;
  @computed('_editableCondition')
  get editableCondition(): ObjectAttributeCondition {
    return this._editableCondition ?? this;
  }
  set editableCondition(condition: ObjectAttributeCondition) {
    this.set('_editableCondition', condition);
  }
  @tracked originalCondition: ObjectAttributeCondition | null = null;
  @tracked childCondition: ObjectAttributeCondition | null = null;
  @tracked parentCondition: ObjectAttributeCondition | null = null;

  protected createCopy() {
    return ObjectAttributeCondition.create({
      objectTypeId: this.objectTypeId,
      name: this.name,
      operator: this.operator,
      comparisonOperator: this.comparisonOperator,
      value: this.value,
      value2: this.value2,
      timestampValue: this.timestampValue,
      timeDirection: this.timeDirection,
      originalCondition: this,
      objectAttributeSource: this.objectAttributeSource,
    });
  }

  @action
  duplicate() {
    let duplicate = ObjectAttributeCondition.create();
    this.copyAllAttributes(this, duplicate);
    return duplicate;
  }

  protected createEmptyCondition() {
    return ObjectAttributeCondition.create({ objectTypeId: this.objectTypeId, parentCondition: this });
  }

  copyAllAttributes(source: ObjectAttributeCondition, target: ObjectAttributeCondition) {
    target.setProperties({
      objectTypeId: this.objectTypeId,
      name: source.name,
      operator: source.operator,
      comparisonOperator: source.comparisonOperator,
      value: source.value,
      value2: source.value2,
      timestampValue: source.timestampValue,
      timeDirection: source.timeDirection,
      objectAttributeSource: source.objectAttributeSource,
    });
  }

  protected createDeepestChildCondition(item: JsonConditionItem) {
    return ObjectAttributeCondition.create({
      objectTypeId: this.objectTypeId,
      operator: this.operator,
      comparisonOperator: this.comparisonOperator,
      value: this.value,
      value2: this.value2,
      timestampValue: this.timestampValue,
      timeDirection: this.timeDirection,
      originalCondition: this,
      objectAttributeSource: this.objectAttributeSource,
      ...this.jsonConditionItemToOverrides(item),
    });
  }

  protected createChildCondition(item: JsonConditionItem, previousCondition: ObjectAttributeCondition) {
    return ObjectAttributeCondition.create({
      objectTypeId: this.objectTypeId,
      ...this.jsonConditionItemToOverrides(item),
      childCondition: previousCondition,
      originalCondition: this,
    });
  }
}
